import React, { useState } from "react";
import { authClient } from "../../../lib/auth-client";
import { logoUrl } from "../../lib/constants";
import { useI18n } from "../../i18n/i18n";
import { Button } from "../button";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { Input } from "../input";

export const AuthPanel = () => {
  const { t } = useI18n();
  const [mode, setMode] = useState<
    "sign-in" | "sign-up" | "forgot-password" | "reset-password"
  >("sign-in");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const callbackUrl = (() => {
    const url = new URL("/bookings", window.location.origin);
    url.searchParams.set("year", String(new Date().getUTCFullYear()));
    return url.toString();
  })();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setStatus(null);
    setIsSubmitting(true);
    try {
      if (mode === "sign-in") {
        const { error: signInError } = await authClient.signIn.email({
          email,
          password,
          callbackURL: callbackUrl,
        });
        if (signInError) {
          setError(signInError.message ?? "sign_in_failed");
        } else {
          window.location.assign(callbackUrl);
        }
      } else if (mode === "sign-up") {
        const { error: signUpError } = await authClient.signUp.email({
          email,
          password,
          name,
          callbackURL: callbackUrl,
        });
        if (signUpError) {
          setError(signUpError.message ?? "sign_up_failed");
        } else {
          window.location.assign(callbackUrl);
        }
      } else if (mode === "forgot-password") {
        const { error: resetError } = await authClient.emailOtp.sendVerificationOtp({
          email,
          type: "forget-password",
        });
        if (resetError) {
          setError(resetError.message ?? "reset_failed");
        } else {
          setMode("reset-password");
          setStatus(t("auth.resetEmailSent", "Check your email for the verification code."));
        }
      } else {
        const { error: resetError } = await authClient.emailOtp.resetPassword({
          email,
          otp,
          password,
        });
        if (resetError) {
          setError(resetError.message ?? "reset_failed");
        } else {
          setMode("sign-in");
          setPassword("");
          setOtp("");
          setStatus(t("auth.resetComplete", "Password reset. You can sign in now."));
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSigningUp = mode === "sign-up";
  const isResetting = mode === "reset-password";
  const isForgotPassword = mode === "forgot-password";

  return (
    <div className="min-h-screen bg-brand p-6">
      <div className="mx-auto max-w-md">
        <div className="mb-6 flex flex-col items-center gap-2 text-center">
          <img src={logoUrl} alt="Hytteflow logo" className="h-12 w-12" loading="lazy" />
          <p className="text-2xl brand-title text-ink">Hytteflow</p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>
              {mode === "sign-in"
                ? t("auth.signIn", "Sign in")
                : mode === "sign-up"
                  ? t("auth.createAccount", "Create account")
                  : mode === "forgot-password"
                    ? t("auth.resetPassword", "Reset password")
                    : t("auth.enterVerification", "Enter verification code")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={handleSubmit}>
              {isSigningUp ? (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-ink" htmlFor="name">
                    {t("auth.name", "Name")}
                  </label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder={t("auth.namePlaceholder", "Name")}
                    required
                  />
                </div>
              ) : null}
              <div className="space-y-2">
                <label className="text-sm font-medium text-ink" htmlFor="email">
                  {t("auth.email", "Email")}
                </label>
                <Input
                  id="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder={t("auth.emailPlaceholder", "you@example.com")}
                  type="email"
                  required
                />
              </div>
              {isResetting ? (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-ink" htmlFor="otp">
                    {t("auth.verificationCode", "Verification code")}
                  </label>
                  <Input
                    id="otp"
                    value={otp}
                    onChange={(event) => setOtp(event.target.value)}
                    placeholder={t("auth.verificationPlaceholder", "123456")}
                    required
                  />
                </div>
              ) : null}
              {isForgotPassword ? null : (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-ink" htmlFor="password">
                    {isResetting
                      ? t("auth.newPassword", "New password")
                      : t("auth.password", "Password")}
                  </label>
                  <Input
                    id="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder={t("auth.passwordPlaceholder", "••••••••")}
                    type="password"
                    required
                  />
                </div>
              )}
              {mode === "sign-in" ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="px-0 text-muted"
                  onClick={() => {
                    setMode("forgot-password");
                    setPassword("");
                    setOtp("");
                    setError(null);
                    setStatus(null);
                  }}
                >
                  {t("auth.forgotPassword", "Forgot password?")}
                </Button>
              ) : null}
              {status ? <p className="text-sm text-muted">{status}</p> : null}
              {error ? <p className="text-sm text-error">{error}</p> : null}
              <Button className="w-full" type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? t("auth.working", "Working...")
                  : mode === "sign-in"
                    ? t("auth.signIn", "Sign in")
                    : mode === "sign-up"
                      ? t("auth.createAccount", "Create account")
                      : mode === "forgot-password"
                        ? t("auth.sendCode", "Send code")
                        : t("auth.resetPassword", "Reset password")}
              </Button>
            </form>
            {mode === "sign-in" || mode === "sign-up" ? (
              <div className="mt-4 flex items-center justify-between text-sm text-muted">
                <span>
                  {mode === "sign-in"
                    ? t("auth.needAccount", "Need an account?")
                    : t("auth.haveAccount", "Already have an account?")}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setMode((current) => (current === "sign-in" ? "sign-up" : "sign-in"))
                  }
                >
                  {mode === "sign-in"
                    ? t("auth.signUp", "Sign up")
                    : t("auth.signIn", "Sign in")}
                </Button>
              </div>
            ) : (
              <div className="mt-4 flex items-center justify-between text-sm text-muted">
                <span>{t("auth.rememberedPassword", "Remembered your password?")}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setMode("sign-in");
                    setOtp("");
                    setPassword("");
                    setError(null);
                    setStatus(null);
                  }}
                >
                  {t("auth.backToSignIn", "Back to sign in")}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
